// @ts-strict-ignore
/* eslint-disable @typescript-eslint/no-use-before-define */

import React, {useEffect, useState} from 'react';
import isNumber from 'lodash/isNumber';
import {clsx} from 'clsx';

import {ButtonReact} from 'common/components/button';
import {ButtonTheme, ButtonType} from 'common/components/button/constants';
import {SvgIconType} from 'common/components/svg-icon';
import {Placement} from 'common/components/tooltip/types';
import {
  generateFilterTrackingProps,
  transformFilterToPropertyItem,
} from 'common/query-builder/query-block/filter-menu/util';
import {isMultiAttributionFilter} from 'common/query-builder/query-builder/util';
import {Action, EntryPart, isObjectEntry, QueryFilterEntry} from 'common/query-builder/query-entry-section/types';
import {FullPropertyType, PropertyType, ResourceType, SPECIAL_EVENTS} from 'common/report/constants';
import {
  getIconForPropertyType,
  isExperimentPropertyName,
  isLatestScd,
  isScdPropertyName,
} from 'common/report/util/properties';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {Segmentation} from 'common/types/reports/query';
import {ActionType, trackLearningAction} from 'common/util/server-tracking';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {isFirstTimeEventFilter, isLookupPropertyFilter} from 'common/widgets/property-filter-menu/models/utils';
import {hasComputedListFilters, isListOfObjectsFilter} from 'common/widgets/property-filter-menu/utils';
import {
  ControlBarAction,
  ControlBarActionItems,
  ControlBarActionValue,
} from 'common/query-builder/query-entry-section/constants';
import {getEntryWithProfileHistory} from 'common/query-builder/query-entry-section/util/profile-history';
import {isProperty, Property} from 'common/query-builder/query-block/properties-menu/types';
import {segmentationToPropertyItem} from 'common/query-builder/query-block/properties-menu/util';
import {unregisterTempLookupTableMapping} from 'common/report/stores/lookup-tables';
import {TrackingEvent} from 'common/query-builder/query-entry-section/tracking';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {getPropertySelectionFromProperty} from 'common/query-builder/query-entry-section/util/behavior';
import {formatDate} from 'common/util/date';
import {useTranslation} from 'common/i18n/hooks';
import {isExperimentSegment} from 'common/query-builder/query-block/segment-menu/types';

import {BaseEntryRow} from '../base';
// eslint-disable-next-line import/no-cycle
import {BehavioralSubFilters, BehavioralSubItem, PropertySelector, RevenueSubItem} from '../behavior/template';
import {
  getIconForBehavior,
  isAggregateUserFreqByGroupSegmentation,
  isEventBasedBehavioralSegmentation,
  isFrequencyBehavior,
  isMultiAttributionBehavior,
  isRevenueBehaviorSegmentation,
  isTTCSegmentation,
} from '../behavior/utils';
import {
  canMapPropToLookupTable,
  canUnmapPropFromLookupTable,
  getEntryPropertyType,
  getPropertyType,
  isComputedListPropertyEntry,
  mapLookupTable,
  populateListPropertyAttrs,
  populateProfileHistoryOptions,
  promptRename,
  setPropertyType,
} from '../row-util';
import {EntryRowProps} from '../types';
import {BehavioralStepSelectionSubItem} from '../behavior/behavioral-step-selection-subitem';
import {AggregateUserFrequencyByGroupSubItem} from '../behavior/aggregate-user-frequency-by-group-subitem';
import {OPERATORS_WITH_UNIT} from './constants';
import {FilterParts} from './parts';
import {createFilter} from './utils';
import {helpers} from './helpers';

export function FilterEntryRow(props: EntryRowProps<QueryFilterEntry>) {
  const [entryEl, setEntryEl] = useState<HTMLDivElement>(null);

  const i18n = useTranslation();
  const {t} = i18n;

  const filter = createFilter(props);

  let hasHourOrMinute = false;
  if (filter.filterValue && typeof filter.filterValue === `object` && `window` in filter.filterValue) {
    hasHourOrMinute =
      filter.filterValue?.window?.unit === TimeUnit.Minute || filter.filterValue?.window?.unit === TimeUnit.Hour;
  }
  const [isTimePickerEnabled, setTimePickerEnabled] = useState(hasHourOrMinute);

  const hasProperty = filter.hasSelectedProperty();
  const isFirstTimeFilter = isFirstTimeEventFilter(filter);
  const isClearAction =
    props.entry.quickDelete && props.entry.clearable && props.numStagedRows === 2 && !props.hasStagedRow;
  const hasConjunction =
    props.hasNewUI && props.renderSuffix && props.sectionProps.entries.length > 1 && props.index === 0;
  const isFirstListItemFilter = !!props.sectionProps.listProperty && props.index === 0;
  const isPlaceholder = helpers.isPlaceholder(props);
  const propertyItem = transformFilterToPropertyItem(props.entry.event);

  useEffect(() => {
    // on first mount, focus the filter value section if we're a placeholder with a property
    // autofocus logic should be shifted to this technique (to avoid state race conditions inside of query-entry-section, but this fixes this for filters for now)
    if (isPlaceholder && hasProperty) {
      props.setActivePart(EntryPart.FilterValue, `open`);
    }
    // purposely not filling out dependency array to track fresh mounts only. this should be wrapped in a custom hook as to centralize across rows
  }, []);

  const getSelectorIcon = (): SvgIconType => {
    if (props.entry.inheritedFromBoard) {
      return `brand-dashboard-gray`;
    }

    if (props.entry.filterOnly) {
      return null;
    }

    if (filter.resourceType === ResourceType.Cohort) {
      return `community`;
    }

    if (isExperimentSegment(filter)) {
      return `experiment`;
    }

    if (isEventBasedBehavioralSegmentation(filter) || isMultiAttributionBehavior(filter.behavior)) {
      return getIconForBehavior(filter.behavior, true);
    }

    if (!filter.hasSelectedProperty()) {
      return `search`;
    }

    if (isFirstTimeFilter) {
      return `time`;
    }

    return getIconForPropertyType(getPropertyType(props.entry.event), {
      isCustom: Boolean(filter?.customProperty),
      isScd: !!filter.scdEvent,
    });
  };

  const handlePropertySelect = (selection: PropertyFilter, queryReason?: QueryReason, trackingProps?: Obj) => {
    selection = populateListPropertyAttrs(selection);
    if (props.isProfileHistoryEnabled) {
      selection = populateProfileHistoryOptions(selection, props.sectionProps.hideEvents);
    }

    // persist the selected filter operator and filter value if changing between same property types
    if (selection.resourceType !== ResourceType.Cohort && filter.propertyType === selection.propertyType) {
      selection.filterValue = filter.filterValue;
      selection.filterOperator = filter.filterOperator;

      if (selection.joinPropertyType === PropertyType.List || isLookupPropertyFilter(filter)) {
        selection.listQuantifier = filter.listQuantifier;
      }
    }

    updateEntry(selection, {trackingProps, queryReason});
  };

  const getNextEntryPart = (entry: PropertyFilter) => {
    let entryPart = EntryPart.FilterValue;
    if (isEventBasedBehavioralSegmentation(entry)) {
      if (!entry.behavior.aggregationOperator) {
        entryPart = EntryPart.Aggregator;
      } else if (!entry.behavior.event.value) {
        entryPart = EntryPart.SubValue;
      }
    } else if (isRevenueBehaviorSegmentation(entry) && !entry.behavior.property.value) {
      entryPart = EntryPart.SubValue;
    } else if (isAggregateUserFreqByGroupSegmentation(entry) && !entry.behavior.filters[0].behavior?.property?.value) {
      entryPart = EntryPart.SubValue;
    }
    return entryPart;
  };

  const onChangeOperator = () => {
    // focus on filter value when operator changes filter to an invalid state
    if (!filter.isValid()) {
      props.setActivePart(EntryPart.FilterValue, `open`);
    }
  };

  const updateEntry = (
    propFilter: PropertyFilter,
    {trackingProps, queryReason}: Partial<{trackingProps: Obj; queryReason: QueryReason}> = {},
  ) => {
    const updatedEntry: QueryFilterEntry = {
      ...props.entry,
      event: propFilter instanceof PropertyFilter ? propFilter.getAttributes() : propFilter,
    };

    const isStagingChanges = props.isStagingEntryChanges(props.entry, updatedEntry);
    if (isStagingChanges || !hasProperty || propFilter.behavior) {
      // when changing a committed filter to an invalid filter, or when the filter property
      // is selected for the first time, set focus to the filter value selection
      props.setActivePart(getNextEntryPart(propFilter), `open`);
    }

    // The filter-menu will pass through queryReason in which case we should use that queryReason.
    queryReason = queryReason || QueryReason.QbFilterUpdate;
    const updateStatus = props.updateEntry(Action.Edit, updatedEntry, {
      trackingProps: trackingProps ?? generateFilterTrackingProps(propFilter),
      queryReason,
    });

    if (queryReason !== QueryReason.QbAutoTriggered && updateStatus.dispatchedUpdate && !props.entry.suppressTracking) {
      // We don't want to track learning action until the complete filter has been created
      trackLearningAction({
        projectId: props.sectionProps.projectId,
        reportName: props.sectionProps.trackName,
        actionType: ActionType.Filter,
        resourceType: propFilter.resourceType,
      });
    }
  };

  const performTypecast = (toType: FullPropertyType) => {
    // do nothing if selecting the same type
    const currentType = getPropertyType(props.entry.event);
    if (currentType.type === toType.type && currentType.isList === toType.isList) {
      return;
    }

    const event = setPropertyType(props.entry.event, toType, true);

    props.updateEntry(Action.Edit, {
      ...props.entry,
      event,
    });
  };

  const handleControlSelect = (value: ControlBarActionValue) => {
    switch (value.action) {
      case ControlBarAction.Typecast:
        performTypecast(value.toType);
        break;
      case ControlBarAction.ProfileHistory:
        props.updateEntry(Action.Edit, getEntryWithProfileHistory(props.entry, value.toType), {
          trackingProps: {'Profile History Type': value.toType},
        });
        break;
      case ControlBarAction.AddScdMeasurements:
        props.updateEntry(Action.Edit, {
          ...props.entry,
          event: {
            ...props.entry.event,
            scdOptions: {
              type: `latest`,
              dateRange: {
                type: DateRangeType.On,
                to: formatDate(new Date(), {iso: true}),
                from: formatDate(new Date(), {iso: true}),
              },
            },
          },
        });
        break;
      case ControlBarAction.RemoveScdMeasurements: {
        // deleting scd options
        const {scdOptions, ...event} = props.entry.event;
        props.updateEntry(Action.Edit, {
          ...props.entry,
          event,
        });
        break;
      }
      case ControlBarAction.MapLookupTable:
        mapLookupTable(props, props.entry.event as Segmentation);
        break;
      case ControlBarAction.UnmapLookupTable:
        unregisterTempLookupTableMapping(segmentationToPropertyItem(props.entry.event as Segmentation));
        props.dispatchTrack(TrackingEvent.UnmappedTable, props.getEntryTrackingProps());

        addToast({
          message: `Removed temporary lookup table mapping`,
          type: ToastType.DESTRUCTIVE,
        });
        break;
      default:
        props.onControlSelect(value);
    }
  };

  const getUnit = () => {
    const filterValue = props.entry.event?.filterValue;
    // For lists we just put 2 to represent that it's always plural
    const val = Array.isArray(filterValue) ? 2 : props.entry.event?.filterValue;
    if (props.hasNewUI && isAggregateUserFreqByGroupSegmentation(props.entry.event as Segmentation)) {
      return `Users`;
    } else if (
      // TODO disable for now while we work on diff designs
      isNumber(val) &&
      isFrequencyBehavior(props.entry.event?.behavior) &&
      OPERATORS_WITH_UNIT.includes(props.entry.event.filterOperator)
    ) {
      return null;
      // return pluralize(`time`, val);
    }
    return null;
  };

  const selectorRenderer = ({rowActions}) => {
    let eventNames = props.sectionProps.eventNames;
    // For attribution filters, we need to query against all events because due
    // to the nature of attribution, the values are spread across all events, not
    // just the ones included in the query.
    if (isMultiAttributionFilter(filter)) {
      eventNames = [...eventNames, SPECIAL_EVENTS.ALL_EVENTS];
      // For SCD filters we just want the values from the SCD event itself
    } else if (filter.scdEvent) {
      eventNames = [filter.scdEvent];
    }
    const filterParts = (
      <FilterParts
        filter={filter}
        hideListQuantifier={props.entry.hideListQuantifier}
        hasNewUI={props.hasNewUI}
        isValueActive={props.activePart === EntryPart.FilterValue}
        setActiveEntry={(state) => props.setActivePart(EntryPart.FilterValue, state)}
        updateEntry={(propFilter) => updateEntry(propFilter)}
        onValueSelectMenuChange={(ev) => props.onSelectMenuChange(ev, EntryPart.FilterValue)}
        onChangeOperator={onChangeOperator}
        operatorOverrides={props.entry.operatorOverrides}
        hasPlaceholder={props.placeholder?.[0] === EntryPart.FilterValue}
        eventNames={eventNames}
        projectId={props.sectionProps.projectId}
        workspaceId={props.sectionProps.workspaceId}
        userId={props.sectionProps.userId}
        dataGroupId={props.sectionProps.dataGroupId}
        readonly={props.sectionProps.readonly}
        withQuickDelete={props.entry.filterOnly && props.entry.quickDelete}
        withQuickRename={props.entry.filterOnly && props.entry.quickRename}
        showTypecastControl={!props.entry.filterOnly && filter.resourceType !== ResourceType.Cohort}
        disabledTypecastTypes={props.sectionProps.disabledTypecastTypes}
        rowActions={rowActions}
        unit={getUnit()}
        isProfileHistoryEnabled={props.isProfileHistoryEnabled}
        hideEvents={props.sectionProps.hideEvents}
        isTimePickerEnabled={isTimePickerEnabled}
        setTimePickerEnabled={setTimePickerEnabled}
        valueDisabled={props.entry.valueDisabled}
        valueTooltip={props.entry.valueTooltip}
      />
    );

    return props.entry.filterOnly ? (
      <>
        {props.entry.quickDelete && (
          <ButtonReact
            className={clsx(`mp-query-entry-delete-row`, {
              'is-clear-action': isClearAction,
            })}
            elref='filterDeleteButton'
            theme={ButtonTheme.Tertiary}
            icon='x'
            type={ButtonType.Destructive}
            tooltip={{
              'delay-in': 10,
              'delay-out': 10,
              placement: Placement.Bottom,
              text: ({t: trans}) => trans(`filters.menu.remove-values`),
            }}
            onClick={() => props.removeEntry()}
          />
        )}
        {props.entry.quickRename && (
          <ButtonReact
            className='mp-query-entry-rename-row'
            elref='filterRenameButton'
            theme={ButtonTheme.Tertiary}
            icon='edit'
            type={ButtonType.Base}
            tooltip={{
              'delay-in': 10,
              'delay-out': 10,
              placement: Placement.Bottom,
              text: ({t: trans}) => trans(`filters.menu.rename-values`),
            }}
            onClick={() => promptRename(props, i18n)}
          />
        )}
        {filterParts}
      </>
    ) : (
      <>
        {/** main selector */}

        <PropertySelector
          {...props}
          // only put in icons for cohort builder
          queryBlockConfig={{
            ...props.queryBlockConfig,
            listProperty: props.sectionProps.listProperty,
          }}
          selectorIcon={props.hasNewUI ? null : getSelectorIcon()}
          contextPanelParentElement={entryEl}
          onSelect={(selectedItem, queryReason) => {
            const selected =
              props.entry.event instanceof PropertyFilter
                ? props.entry.event.getAttributes()
                : (props.entry.event as Segmentation);
            const handledEvent = getPropertySelectionFromProperty(selectedItem, selected, {
              isForFilter: true,
              reportName: props.sectionProps.reportName,
              dataGroupId: props.sectionProps.dataGroupId,
              chartType: props.sectionProps.chartType,
              dateUnit: props.sectionProps.dateUnit,
            }) as PropertyFilter;
            handlePropertySelect(handledEvent, queryReason);
          }}
          isPlaceholder={isPlaceholder}
          selected={
            props.entry.event instanceof PropertyFilter
              ? props.entry.event.getAttributes()
              : (props.entry.event as Segmentation)
          }
          isForFilter
        />
        {/* renders behavioral controls (time & aggregation) for the selected property */}
        {isEventBasedBehavioralSegmentation(props.entry.event as Segmentation) && props.entry.event?.[`behavior`] && (
          <BehavioralSubItem
            {...props}
            contextPanelParentElement={entryEl}
            selected={
              props.entry.event instanceof PropertyFilter
                ? props.entry.event.getAttributes()
                : (props.entry.event as Segmentation)
            }
            onSelect={handlePropertySelect}
            isPlaceholder={isPlaceholder}
          />
        )}

        {isRevenueBehaviorSegmentation(props.entry.event as Segmentation) && (
          <RevenueSubItem
            {...props}
            contextPanelParentElement={entryEl}
            selected={
              props.entry.event instanceof PropertyFilter
                ? props.entry.event.getAttributes()
                : (props.entry.event as Segmentation)
            }
            isForFilter
            onSelect={handlePropertySelect}
            isPlaceholder={isPlaceholder}
          />
        )}

        {isAggregateUserFreqByGroupSegmentation(props.entry.event as Segmentation) && (
          <AggregateUserFrequencyByGroupSubItem
            {...props}
            contextPanelParentElement={entryEl}
            selected={
              props.entry.event instanceof PropertyFilter
                ? props.entry.event.getAttributes()
                : (props.entry.event as Segmentation)
            }
            onSelect={handlePropertySelect}
            isPlaceholder={isPlaceholder}
            isFilter
            extraFilters={filterParts}
          />
        )}

        {isTTCSegmentation(props.entry.event as Segmentation) && (
          <BehavioralStepSelectionSubItem
            className='step-selection'
            stepCount={props.sectionProps.stepCount}
            selected={
              props.entry.event instanceof PropertyFilter
                ? props.entry.event.getAttributes()
                : (props.entry.event as Segmentation)
            }
            onChange={handlePropertySelect}
          />
        )}
        {/* filter parts */}
        {hasProperty && !isAggregateUserFreqByGroupSegmentation(props.entry.event as Segmentation) ? filterParts : null}
      </>
    );
  };

  const suffixRenderer = () => (
    <>
      {/* renders filters applied to a behavior's event */}
      {<BehavioralSubFilters {...props} onSelect={handlePropertySelect} selected={props.entry.event as Segmentation} />}
    </>
  );

  const actionsRenderer = () => (
    <>
      {isListOfObjectsFilter(filter) && hasComputedListFilters(filter) === false && (
        <ButtonReact
          elref='addItemFilterButton'
          theme={ButtonTheme.Tertiary}
          icon='plus'
          role='button'
          tooltip={{
            text: `Add`,
            placement: Placement.Top,
          }}
          onClick={() => props.showPlaceholder(EntryPart.FilterValue)}
        />
      )}
    </>
  );

  const showScdOverflowControls =
    isScdPropertyName(props.entry.event?.propertyName) || isExperimentPropertyName(props.entry.event?.propertyName);

  return (
    <BaseEntryRow
      ref={setEntryEl}
      {...props}
      hideActions={
        props.entry.filterOnly ||
        props.entry.inheritedFromBoard ||
        (!props.hasNewUI && (isListOfObjectsFilter(filter) || isFirstListItemFilter))
      }
      extraEntryClass={{
        'is-filter-only': props.entry.filterOnly,
        'is-first-time-filter': filter && isFirstTimeFilter,
        'has-conjunction': hasConjunction,
      }}
      showDefaultMetricUnitSelector={false}
      renderSelector={selectorRenderer}
      renderSuffix={props.renderSuffix ?? suffixRenderer}
      renderActions={actionsRenderer}
      isPlaceholder={isPlaceholder}
      entryIcon={getSelectorIcon()}
      onControlSelect={handleControlSelect}
      disabled={props.entry.inheritedFromBoard}
      tooltip={props.entry.inheritedFromBoard ? t(`query-builder.filter-inherited-from-board`) : ''}
      overflowControls={[
        ...props.getCommonOverflowControls(),
        // profile history
        props.isProfileHistoryEnabled &&
          !isFirstTimeFilter &&
          filter.resourceType === ResourceType.User &&
          ControlBarActionItems[ControlBarAction.ProfileHistory]({
            currentProfileHistoryType: props.entry.event.profileHistoryOptions?.type,
            hideEvents: props.sectionProps.hideEvents,
            selectorType: props.entry.type,
          }),
        // experiments
        !props.hasNewUI &&
          showScdOverflowControls &&
          !props.entry.event.scdOptions &&
          !isFirstTimeFilter &&
          filter.resourceType === ResourceType.Event &&
          ControlBarActionItems[ControlBarAction.AddScdMeasurements],
        !props.hasNewUI &&
          showScdOverflowControls &&
          props.entry.event.scdOptions &&
          !isFirstTimeFilter &&
          filter.resourceType === ResourceType.Event &&
          ControlBarActionItems[ControlBarAction.RemoveScdMeasurements],
        // scd
        !props.hasNewUI &&
          showScdOverflowControls &&
          (!props.entry.event.scdOptions || isLatestScd(props.entry.event.scdOptions)) &&
          !isFirstTimeFilter &&
          filter.resourceType === ResourceType.User &&
          ControlBarActionItems[ControlBarAction.AddScdMeasurements],
        !props.hasNewUI &&
          showScdOverflowControls &&
          props.entry.event.scdOptions &&
          !isLatestScd(props.entry.event.scdOptions) &&
          !isFirstTimeFilter &&
          filter.resourceType === ResourceType.User &&
          ControlBarActionItems[ControlBarAction.RemoveScdMeasurements],
        // typecasting
        hasProperty &&
          !isComputedListPropertyEntry(props.entry) &&
          !props.sectionProps.disableTypecast &&
          !isFirstTimeFilter &&
          filter.resourceType !== ResourceType.Cohort &&
          ControlBarActionItems[ControlBarAction.Typecast](getEntryPropertyType(props.entry), {
            disabledTypes: props.sectionProps.disabledTypecastTypes,
          }),
        // map to lookup table
        !isPlaceholder &&
          !props.sectionProps.disableMapToLookupTable &&
          !isObjectEntry(props.entry) &&
          !Array.isArray(propertyItem) &&
          isProperty(propertyItem) &&
          canMapPropToLookupTable(propertyItem) &&
          ControlBarActionItems[ControlBarAction.MapLookupTable],
        // unmap from lookup table
        !isPlaceholder &&
          canUnmapPropFromLookupTable(propertyItem as Property) &&
          ControlBarActionItems[ControlBarAction.UnmapLookupTable],
      ]}
    />
  );
}
